import { useState, useEffect, useCallback, useRef } from "react";
import { ReviewTabHead } from "@components/ReviewTabHead/ReviewTabHead";
import { QuestionTabHead } from "@components/QuestionTabHead/QuestionTabHead";
import { ReviewBlock } from "@components/ReviewBlock/ReviewBlock";
import { QuestionBlock } from "@components/QuestionBlock/QuestionBlock";
import { Pagination } from "@components/Pagination/Pagination";
import styles from "./TabbedWidget.module.css";
import { Question, Review, ReviewStats } from "@types";
import reviewsService from "@api/reviewsService";
// import debugService from "@api/debugService";

// Определяем тип для вкладок
type TabType = "reviews" | "qa";

const ITEMS_PER_PAGE = 5;

export const TabbedWidget = ({
  productArticle,
  productName,
}: {
  productArticle?: string;
  productName?: string;
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("reviews");
  const [currentPage, setCurrentPage] = useState(1);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalReviewsData, setTotalReviewsData] = useState<ReviewStats>({
    averageRating: 0,
    totalReviews: 0,
    ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  });
  const [totalReviewPages, setTotalReviewPages] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [totalQuestionPages, setTotalQuestionPages] = useState(1);

  // Флаги для отслеживания инициализации данных
  const questionsInitializedRef = useRef(false);
  const reviewsInitializedRef = useRef(false);
  const statsInitializedRef = useRef(false);

  // Загрузка отзывов
  const getReviews = useCallback(
    async (page: number) => {
      try {
        const reviewsResponse = await reviewsService.getReviews({
          page: page - 1, // API использует индексацию с 0
          size: ITEMS_PER_PAGE,
          product_name: productName,
          product_id__in: productArticle,
        });

        setReviews(reviewsResponse.content);
        setTotalReviewPages(reviewsResponse.totalPages);
        reviewsInitializedRef.current = true;

        // Обновляем общее количество отзывов из ответа API, если доступно
        if (reviewsResponse.totalElements !== undefined) {
          setTotalReviewsData((prevStats) => ({
            ...prevStats,
            totalReviews: reviewsResponse.totalElements,
          }));
        }

        return reviewsResponse;
      } catch (err) {
        console.error("Ошибка при загрузке отзывов:", err);
        throw err;
      }
    },
    [productArticle, productName]
  );

  // Загрузка вопросов
  const getQuestions = useCallback(
    async (page: number) => {
      try {
        const questionsResponse = await reviewsService.getQuestions({
          page: page - 1, // API использует индексацию с 0
          size: ITEMS_PER_PAGE,
          product_name: productName,
          product_id__in: productArticle,
        });

        setQuestions(questionsResponse.content);
        setTotalQuestionPages(questionsResponse.totalPages);
        setTotalQuestions(questionsResponse.totalElements);
        questionsInitializedRef.current = true;

        return questionsResponse;
      } catch (err) {
        console.error("Ошибка при загрузке вопросов:", err);
        throw err;
      }
    },
    [productArticle, productName]
  );

  // Загрузка статистики
  const getStats = useCallback(async () => {
    // Если нет ни артикула, ни имени продукта, не выполняем запрос
    if (!productName && !productArticle) {
      console.warn("Невозможно загрузить статистику: не указан артикул или имя продукта");
      return null;
    }

    // Используем имя продукта, если доступно, иначе артикул
    const productIdentifier = productName || productArticle;
    
    if (!productIdentifier) {
      console.warn("Невозможно загрузить статистику: идентификатор продукта пустой");
      return null;
    }

    try {
      const productStats = await reviewsService.getProductStats(productIdentifier);
      setTotalReviewsData(productStats);
      statsInitializedRef.current = true;
      console.log("Product stats fetched successfully", productStats);
      return productStats;
    } catch (statsError) {
      const error = statsError as Error;
      console.error("Ошибка при загрузке статистики:", error);
      throw statsError;
    }
  }, [productArticle, productName]);

  // Публичная функция для обновления отзывов после отправки формы
  const refreshReviews = useCallback(() => {
    // Обновляем текущую страницу отзывов
    getReviews(currentPage);

    // Обновляем статистику отзывов
    getStats();
  }, [currentPage, getReviews, getStats]);

  // Публичная функция для обновления вопросов после отправки формы
  const refreshQuestions = useCallback(() => {
    getQuestions(currentPage);
  }, [currentPage, getQuestions]);

  // Инициализация данных при первой загрузке
  useEffect(() => {
    if (!productArticle && !productName) return;

    const initializeData = async () => {
      setIsLoading(true);
      try {
        // Загружаем статистику только один раз при инициализации
        if (!statsInitializedRef.current) {
          await getStats();
        }

        // Всегда получаем количество отзывов и вопросов при инициализации
        const tabDataPromises = [];

        // Загрузка отзывов
        if (!reviewsInitializedRef.current) {
          if (activeTab === "reviews") {
            // Полная загрузка для активной вкладки
            tabDataPromises.push(getReviews(currentPage));
          } else {
            // Только количество для неактивной вкладки
            tabDataPromises.push(
              reviewsService
                .getReviews({
                  page: 0,
                  size: 1,
                  product_name: productName,
                  product_id__in: productArticle,
                })
                .then((response) => {
                  setTotalReviewsData((prevStats) => ({
                    ...prevStats,
                    totalReviews: response.totalElements,
                  }));
                  setTotalReviewPages(response.totalPages);
                  reviewsInitializedRef.current = true;
                })
                .catch((err) => {
                  console.error(
                    "Ошибка при получении количества отзывов:",
                    err
                  );
                })
            );
          }
        }

        // Загрузка вопросов
        if (!questionsInitializedRef.current) {
          if (activeTab === "qa") {
            // Полная загрузка для активной вкладки
            tabDataPromises.push(getQuestions(currentPage));
          } else {
            // Только количество для неактивной вкладки
            tabDataPromises.push(
              reviewsService
                .getQuestions({
                  page: 0,
                  size: 1,
                  product_name: productName,
                  product_id__in: productArticle,
                })
                .then((response) => {
                  setTotalQuestions(response.totalElements);
                  setTotalQuestionPages(response.totalPages);
                  questionsInitializedRef.current = true;
                })
                .catch((err) => {
                  console.error(
                    "Ошибка при получении количества вопросов:",
                    err
                  );
                })
            );
          }
        }

        await Promise.all(tabDataPromises);
      } catch (err) {
        const error = err as Error;
        console.error("Ошибка при инициализации данных:", error);
        setError("Не удалось загрузить данные");
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [
    productArticle,
    getStats,
    getReviews,
    getQuestions,
    activeTab,
    currentPage,
    productName,
  ]);

  // Обновление данных при смене страницы или вкладки
  useEffect(() => {
    // Пропускаем первичную загрузку, которую обрабатывает initializeData
    if (
      (!productArticle && !productName) ||
      (activeTab === "reviews" && !reviewsInitializedRef.current) ||
      (activeTab === "qa" && !questionsInitializedRef.current)
    ) {
      return;
    }

    const loadCurrentTabData = async () => {
      setIsLoading(true);
      try {
        if (activeTab === "reviews") {
          await getReviews(currentPage);
        } else {
          await getQuestions(currentPage);
        }
      } catch (err) {
        const error = err as Error;
        console.error(
          `Ошибка при загрузке ${
            activeTab === "reviews" ? "отзывов" : "вопросов"
          }:`,
          error
        );
        setError(
          `Не удалось загрузить ${
            activeTab === "reviews" ? "отзывы" : "вопросы"
          }`
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadCurrentTabData();
  }, [
    activeTab,
    currentPage,
    getQuestions,
    getReviews,
    productArticle,
    productName,
  ]);

  // Получение данных текущей вкладки
  const getCurrentTabData = () => {
    return activeTab === "reviews" ? reviews : questions;
  };

  // Получение общего количества страниц для текущей вкладки
  const getTotalPages = () => {
    if (activeTab === "reviews") {
      return totalReviewPages;
    } else {
      return totalQuestionPages;
    }
  };

  // Обработка смены вкладки
  const handleTabChange = (tab: TabType) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      setCurrentPage(1); // Сброс на первую страницу при смене вкладки
    }
  };

  // Обработка смены страницы
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Отрисовка элемента в зависимости от типа
  const renderItem = (item: Review | Question) => {
    if (activeTab === "reviews") {
      return <ReviewBlock key={item.id} review={item as Review} />;
    } else {
      return <QuestionBlock key={item.id} question={item as Question} />;
    }
  };

  // Определяем, есть ли данные для отображения
  const hasNoData = getCurrentTabData().length === 0 && !isLoading && !error;

  // Отображаем ошибку, если она есть
  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  // Глобальные функции для обновления данных
  // Добавляем функции в глобальную область видимости, если это необходимо
  if (typeof window !== "undefined") {
    Object.assign(window, {
      refreshReviews,
      refreshQuestions,
    });
  }

  return (
    <div className={styles.container}>
      {/* Заголовок с вкладками */}
      <nav className={styles.tabNav} aria-label="Tabs">
        <button
          className={`${styles.tab} ${
            activeTab === "reviews" ? styles.tabActive : ""
          }`}
          onClick={() => handleTabChange("reviews")}
        >
          Отзывы ({totalReviewsData.totalReviews})
        </button>
        <button
          className={`${styles.tab} ${
            activeTab === "qa" ? styles.tabActive : ""
          }`}
          onClick={() => handleTabChange("qa")}
        >
          Вопросы ({totalQuestions})
        </button>
      </nav>

      <div className={styles.content}>
        {/* Отображение информации о текущей вкладке */}
        {activeTab === "reviews" ? (
          <ReviewTabHead
            reviewsData={totalReviewsData}
            productId={productArticle}
            productName={productName}
            onReviewSubmit={refreshReviews}
          />
        ) : (
          <QuestionTabHead
            productId={productArticle}
            productName={productName}
            onQuestionSubmit={refreshQuestions}
          />
        )}

        {/* Список отзывов/вопросов или сообщение об отсутствии данных */}
        <div className={styles.reviewsContainer}>
          {isLoading ? (
            <div>Загрузка...</div>
          ) : hasNoData ? (
            <div className={styles.noData}>
              {activeTab === "reviews"
                ? "Отзывов пока нет. Будьте первым, кто оставит отзыв!"
                : "Вопросов пока нет. Задайте первый вопрос!"}
            </div>
          ) : (
            <div className={styles.reviewsList}>
              {getCurrentTabData().map(renderItem)}
            </div>
          )}
        </div>

        {/* Пагинация */}
        {getTotalPages() > 1 && !isLoading && (
          <Pagination
            currentPage={currentPage}
            totalPages={getTotalPages()}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};
